import { type User } from "@/gql/auth";
import { useBuyerStore } from "@/stores/buyer/buyer-store";
import { useSellerStore } from "@/stores/seller/seller-store";
import { trackLogin } from "@/utils/analytics/track-login";
import { logger } from "@/utils/logger";
import { cacheUser, clearAllCache, getCachedUser, type CachedUser } from "@/utils/user-preferences";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserGroup {
	id: string;
	relationship: string;
}

export interface AuthState {
	// State
	isAuthenticated: boolean;
	user: User | null;
	token: string | null;
	isHydrated: boolean;

	// Actions
	setAuthData: (token: string, user: User) => Promise<void>;
	clearAuthData: () => Promise<void>;
	initializeAuth: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTH_TOKEN_KEY = "settlin_auth_token";
const AUTH_USER_KEY = "settlin_auth_user";

// ---------------------------------------------------------------------------
// Storage utilities (dual-layer: localStorage for token, IndexedDB for user)
// ---------------------------------------------------------------------------

export const authStorage = {
	getToken: (): string | null => {
		if (typeof window === "undefined") return null;
		return localStorage.getItem(AUTH_TOKEN_KEY);
	},

	getUser: async (): Promise<User | null> => {
		if (typeof window === "undefined") return null;

		try {
			// Try IndexedDB first
			const cachedUser = await getCachedUser();
			if (cachedUser) {
				return cachedUser as User;
			}

			// Fall back to localStorage for migration
			const userStr = localStorage.getItem(AUTH_USER_KEY);
			if (userStr) {
				const user = JSON.parse(userStr);
				// Migrate to IndexedDB
				await cacheUser(user);
				localStorage.removeItem(AUTH_USER_KEY);
				return user;
			}

			return null;
		} catch (error) {
			logger.warn("Failed to get user from IndexedDB, falling back to localStorage:", error);
			const userStr = localStorage.getItem(AUTH_USER_KEY);
			try {
				return userStr ? JSON.parse(userStr) : null;
			} catch {
				return null;
			}
		}
	},

	setAuth: async (token: string, user: User): Promise<void> => {
		if (typeof window === "undefined") return;

		try {
			localStorage.setItem(AUTH_TOKEN_KEY, token);
			await cacheUser(user as CachedUser);
			localStorage.removeItem(AUTH_USER_KEY);
		} catch (error) {
			logger.warn("Failed to cache user in IndexedDB, falling back to localStorage:", error);

			try {
				localStorage.setItem(AUTH_TOKEN_KEY, token);
				localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
			} catch (fallbackError) {
				throw new Error(`Storage operation failed completely: ${fallbackError}`);
			}
		}
	},

	clearAuth: async (): Promise<void> => {
		if (typeof window === "undefined") return;

		try {
			localStorage.removeItem(AUTH_TOKEN_KEY);
			localStorage.removeItem(AUTH_USER_KEY);
			await clearAllCache();
		} catch (error) {
			logger.warn("Failed to clear cache from IndexedDB:", error);
			localStorage.removeItem(AUTH_TOKEN_KEY);
			localStorage.removeItem(AUTH_USER_KEY);
		}
	},
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractBuyerId(user: User | null): string | null {
	const groups = user?.groups;
	return groups?.find((g: UserGroup) => g.relationship === "buyer")?.id || null;
}

function syncBuyerId(user: User | null) {
	const buyerId = extractBuyerId(user);
	useBuyerStore.getState().setBuyerId(buyerId);
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
	devtools(
		subscribeWithSelector((set, get) => ({
			// Initial state
			isAuthenticated: false,
			user: null,
			token: null,
			isHydrated: false,

			setAuthData: async (token: string, user: User) => {
				try {
					await authStorage.setAuth(token, user);

					set(
						{
							token,
							user,
							isAuthenticated: true,
							isHydrated: true,
						},
						false,
						"setAuthData",
					);

					syncBuyerId(user);
				} catch (error) {
					logger.error("Failed to set auth data:", error);
				}
			},

			clearAuthData: async () => {
				try {
					await authStorage.clearAuth();

					set(
						{
							token: null,
							user: null,
							isAuthenticated: false,
							isHydrated: true,
						},
						false,
						"clearAuthData",
					);

					useBuyerStore.getState().setBuyerId(null);
					useSellerStore.getState().setProperties([]);
				} catch (error) {
					logger.error("Failed to clear auth data:", error);
				}
			},

			initializeAuth: async () => {
				// Guard against double-initialization
				if (get().isHydrated) return;

				try {
					const storedToken = authStorage.getToken();
					const storedUser = await authStorage.getUser();

					syncBuyerId(storedUser);

					set(
						{
							token: storedToken,
							user: storedUser,
							isAuthenticated: !!(storedToken && storedUser),
							isHydrated: true,
						},
						false,
						"initializeAuth",
					);

					// Track session resume if already authenticated
					if (storedToken && storedUser) {
						try {
							await trackLogin({
								userData: {
									id: storedUser.id,
									name: storedUser.name,
									profile: storedUser.profile,
									emails: storedUser.emails,
									phones: storedUser.phones,
									groups: storedUser.groups,
									internal: Boolean(storedUser.internal),
								},
								loginType: "session_resume",
							});
						} catch (trackingError) {
							logger.debug("Session resume tracking failed (non-critical):", trackingError);
						}
					}
				} catch (error) {
					if (error instanceof Error && error.message.includes("Access is denied for this document")) {
						logger.debug("LocalStorage access denied (expected in private mode):", error);
					} else {
						const errorDetails = {
							message: error instanceof Error ? error.message : "Unknown error",
							stack: error instanceof Error ? error.stack : undefined,
							name: error instanceof Error ? error.name : "UnknownError",
							userAgent: typeof window !== "undefined" ? window.navigator?.userAgent : "server",
							indexedDBSupported: typeof window !== "undefined" && "indexedDB" in window,
							localStorageSupported: typeof window !== "undefined" && "localStorage" in window,
							timestamp: new Date().toISOString(),
						};

						logger.error("Failed to initialize auth from storage:", errorDetails);
					}

					set(
						{
							token: null,
							user: null,
							isAuthenticated: false,
							isHydrated: true,
						},
						false,
						"initializeAuth/error",
					);

					// Clear potentially corrupted data
					try {
						if (typeof window !== "undefined") {
							localStorage.removeItem(AUTH_TOKEN_KEY);
							localStorage.removeItem(AUTH_USER_KEY);
						}
					} catch (cleanupError) {
						logger.warn("Failed to cleanup corrupted auth data:", cleanupError);
					}
				}
			},
		})),
		{ name: "auth-store" },
	),
);
